import { Box } from '@mui/material';
import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { AsyncContent } from '~/components/AsyncContent';
import { useRefDimensions } from '~/hooks/useRefDimensions';
import { useVisible } from '~/hooks/useVisible';
import { ApprovalRowMode } from '~/scenes/MyWorkspace/components/Widgets/components/ApprovalWidget/components/ApprovalWidgetContent/components/CategoryListItem/components/ApprovalRow/component';
import { ApprovalWidgetCategory } from '~/scenes/MyWorkspace/components/Widgets/components/ApprovalWidget/data';
import { ApprovalWidgetData } from '~/scenes/MyWorkspace/components/Widgets/components/ApprovalWidget/types';
import { UseApprovalWidgetDataOutput } from '~/scenes/MyWorkspace/components/Widgets/components/ApprovalWidget/useApprovalWidgetData';
import { formatCurrency } from '~/services/Formatters';

import { CategoryListItem } from './components/CategoryListItem';
import { useSxStyles } from './useStyles';

export type Props = UseApprovalWidgetDataOutput;

export const ApprovalWidgetContent = (props: Props) => {
  const {
    data,
    selection,
    setSelection,
    onDecision,
    isLoadingDetailed,
    failedToLoadDetailed,
    enabledSections,
  } = props;

  const { t } = useTranslation();
  const sxStyles = useSxStyles();

  const widgetContentRef = useRef(null);
  const dimensions = useRefDimensions({
    ref: widgetContentRef,
  });

  const absencesVisible = useVisible(true);
  const allowanceVisible = useVisible(true);
  const benefitVisible = useVisible(true);
  const incentiveVisible = useVisible(true);
  const salaryVisible = useVisible(true);
  const trainingVisible = useVisible(true);

  const getRowMode = useCallback((width: number) => {
    if (width > 450) {
      return ApprovalRowMode.Table;
    } else if (width > 300) {
      return ApprovalRowMode.SemiItem;
    }
    return ApprovalRowMode.Item;
  }, []);

  const commonCategoryListItemProps = {
    rowMode: getRowMode(dimensions.width),
    onSelection: setSelection,
    onDecision,
  };

  return (
    <AsyncContent
      isLoading={false}
      failedToLoad={false}
      customSxStyles={{ errorMessage: sxStyles.errorMessage }}
    >
      <Box sx={sxStyles.list} ref={widgetContentRef}>
        <Box component="ul" sx={sxStyles.ul}>
          {enabledSections.absences && data && (
            <CategoryListItem
              secondCellName={t('time')}
              thirdCellName={t('days')}
              data={data.absences}
              isLoading={isLoadingDetailed.absences}
              failedToLoad={failedToLoadDetailed.absences}
              noData={data?.absences?.length === 0}
              category={ApprovalWidgetCategory.Absences}
              isOpened={absencesVisible.isVisible}
              categoryName={t('absencesWaitingForApproval')}
              categoryThirdValue={(awd: ApprovalWidgetData) =>
                ((!!awd.days || awd.days === 0) && awd.days.toString()) || '-'
              }
              showActionDetails={(widgetData: ApprovalWidgetData) =>
                widgetData.value || ''
              }
              showEndDate={true}
              {...commonCategoryListItemProps}
              selection={selection.absences}
              onToggleOpen={absencesVisible.toggle}
            />
          )}
          {enabledSections.rewarding && data && (
            <CategoryListItem
              secondCellName={t('startDate')}
              thirdCellName={t('amount')}
              data={data.allowance}
              isLoading={isLoadingDetailed.allowance}
              failedToLoad={failedToLoadDetailed.allowance}
              noData={data?.allowance?.length === 0}
              category={ApprovalWidgetCategory.Allowance}
              isOpened={allowanceVisible.isVisible}
              categoryName={t('allowancesWaitingForApproval')}
              categoryThirdValue={(widgetData: ApprovalWidgetData) =>
                `${widgetData.value} ${formatCurrency(
                  widgetData.currency ? widgetData.currency : 'EUR',
                )}`
              }
              showActionDetails={(widgetData: ApprovalWidgetData) =>
                widgetData.type
              }
              {...commonCategoryListItemProps}
              selection={selection.allowance}
              onToggleOpen={allowanceVisible.toggle}
            />
          )}
          {enabledSections.rewarding && data && (
            <CategoryListItem
              secondCellName={t('startDate')}
              thirdCellName={t('amount')}
              data={data.benefit}
              isLoading={isLoadingDetailed.benefit}
              failedToLoad={failedToLoadDetailed.benefit}
              noData={data?.benefit?.length === 0}
              category={ApprovalWidgetCategory.Benefit}
              isOpened={benefitVisible.isVisible}
              categoryName={t('benefitsWaitingForApproval')}
              categoryThirdValue={(widgetData: ApprovalWidgetData) =>
                `${widgetData.value} ${formatCurrency(
                  widgetData.currency ? widgetData.currency : 'EUR',
                )}`
              }
              showActionDetails={(widgetData: ApprovalWidgetData) =>
                widgetData.type
              }
              {...commonCategoryListItemProps}
              selection={selection.benefit}
              onToggleOpen={benefitVisible.toggle}
            />
          )}
          {enabledSections.rewarding && data && (
            <CategoryListItem
              secondCellName={t('startDate')}
              thirdCellName={t('amount')}
              data={data.incentive}
              isLoading={isLoadingDetailed.incentive}
              failedToLoad={failedToLoadDetailed.incentive}
              noData={data?.incentive?.length === 0}
              category={ApprovalWidgetCategory.Incentive}
              isOpened={incentiveVisible.isVisible}
              categoryName={t('incentivesWaitingForApproval')}
              categoryThirdValue={(widgetData: ApprovalWidgetData) =>
                `${widgetData.value} ${formatCurrency(
                  widgetData.currency ? widgetData.currency : 'EUR',
                )}`
              }
              showActionDetails={(widgetData: ApprovalWidgetData) =>
                widgetData.type
              }
              {...commonCategoryListItemProps}
              selection={selection.incentive}
              onToggleOpen={incentiveVisible.toggle}
            />
          )}
          {enabledSections.rewarding && data && (
            <CategoryListItem
              secondCellName={t('startDate')}
              thirdCellName={t('amount')}
              data={data.salary}
              isLoading={isLoadingDetailed.salary}
              failedToLoad={failedToLoadDetailed.salary}
              noData={data?.salary?.length === 0}
              category={ApprovalWidgetCategory.Salary}
              isOpened={salaryVisible.isVisible}
              categoryName={t('salaryWaitingForApproval')}
              categoryThirdValue={(widgetData: ApprovalWidgetData) =>
                `${widgetData.value} ${formatCurrency(
                  widgetData.currency ? widgetData.currency : 'EUR',
                )}`
              }
              showActionDetails={(widgetData: ApprovalWidgetData) =>
                widgetData.type
              }
              {...commonCategoryListItemProps}
              selection={selection.salary}
              onToggleOpen={salaryVisible.toggle}
            />
          )}
          {enabledSections.training && data && (
            <CategoryListItem
              secondCellName={t('startDate')}
              thirdCellName={t('hours')}
              data={data.training}
              isLoading={isLoadingDetailed.training}
              failedToLoad={failedToLoadDetailed.training}
              noData={data?.training?.length === 0}
              category={ApprovalWidgetCategory.Training}
              isOpened={trainingVisible.isVisible}
              categoryName={t('trainingsWaitingForApproval')}
              categoryThirdValue={(widgetData: ApprovalWidgetData) =>
                widgetData.hours?.toString() || ''
              }
              showActionDetails={(widgetData: ApprovalWidgetData) =>
                widgetData.type
              }
              {...commonCategoryListItemProps}
              selection={selection.training}
              onToggleOpen={trainingVisible.toggle}
            />
          )}
        </Box>
      </Box>
    </AsyncContent>
  );
};
